import { JobFile } from '@core/models/job-file';
import { User } from '@core/models/user.model';
import { Pagination } from '@shared/models/pagination.model';
import { EntityMap } from '@shared/models/types';
import { JobGroup } from 'app/modules/job-group/models/job-group.model';
import { TaskFeedSummary } from './task-feed-summary.model';
import { OnGoingJob } from '../on-going-job.model';
import { DashboardItem, DashboardColumn, DashboardRow } from '../dashboard-rows.model';
import { Customer } from '../customer.model';
import { InvoiceFinancialResult } from 'app/modules/dashboard/models/invoice-financial.model';
import { SupplierTripsResponse } from '../supplier-trips.model';
import { SupplierCostAnalysisResponse } from '../supplier-costs.model';
import {
  CompletedJobItem,
  DeliveryInProgressJobItem,
  NotifiedJobItem,
  ContractedJobItem,
  PlannedJobItem,
  PaginationMeta,
  TimeRangeEnum,
  JobSummaryTimeRange,
  JobStatusEnum,
  LoadAssignmentJobItem,
  PendingAcceptanceJobItem,
  AcceptedJobItem,
  TerminatedJobItem
} from '../job-summary.model';
import { CustomerTripsResponse } from '../customer-trips.model';
import { ValuePerKmOrShipmentResult } from '../value-per-km-or-shipment.types';
import { CustomerNodeTreeMap } from '../customer-most-profiyable';
import { CustomerKilometersResponse } from '../customer-kilometers.model';
import { VehicleUtilizationMetric } from '../vehicle-utilization-metric.model';
import { SupplierListResponse, SupplierTimeSeriesResponse } from '../supplier-list.model';
import { TimeInsightsMetadata } from '../time-insights.model';

export interface DashboardStateModel {
  // Common Data
  onGoingJobs: EntityMap<string, JobFile>;
  onGoingJobsPaginationData: Pagination;
  taskFeedSummary: TaskFeedSummary;
  logos: EntityMap<string, string>;
  selectedOnGoingJob: OnGoingJob;
  customers: Customer[];

  // job file
  jobGroupsForJobFileCreation: EntityMap<string, JobGroup>;
  internalCollaborators: User[];

  // Dashboard config example:
  // New structure: Each row has columns, each column has items
  dashboardRows: DashboardRow[];

  revenueFinancialData?: InvoiceFinancialResult;
  costFinancialData?: InvoiceFinancialResult;
  profitFinancialData?: InvoiceFinancialResult;
  supplierTrips?: SupplierTripsResponse;
  supplierCostAnalysis?: SupplierCostAnalysisResponse;
  supplierList?: SupplierListResponse;
  supplierTimeSeries?: SupplierTimeSeriesResponse;
  customerTrips?: CustomerTripsResponse;


  costPerKm?: ValuePerKmOrShipmentResult;
  costPerShipment?: ValuePerKmOrShipmentResult;
  revenuePerKm?: ValuePerKmOrShipmentResult;
  revenuePerShipment?: ValuePerKmOrShipmentResult;
  profitPerKm?: ValuePerKmOrShipmentResult;
  profitPerShipment?: ValuePerKmOrShipmentResult;
  customerKilometers?: CustomerKilometersResponse;
  jobSummary: {
    completedJobs: CompletedJobItem[];
    deliveryInProgressJobs: DeliveryInProgressJobItem[];
    notifiedJobs: NotifiedJobItem[];
    contractedJobs: ContractedJobItem[];
    plannedJobs: PlannedJobItem[];
    // Additional lifecycle stages
    loadAssignmentJobs?: LoadAssignmentJobItem[];
    pendingAcceptanceJobs?: PendingAcceptanceJobItem[];
    acceptedJobs?: AcceptedJobItem[];
    terminatedJobs?: TerminatedJobItem[];
    /** Tenant timezone for dates; browser zone when null. */
    timeZone?: string | null;
    pagination: PaginationMeta;
    timeRange: JobSummaryTimeRange;
    selectedStatus?: JobStatusEnum;
    loading: boolean;
    error: string | null;
    // UI State properties for preserving user interactions
    uiState: {
      activeTab: JobStatusEnum;
      timeRange: TimeRangeEnum;
      dateRange: {
        startDate: Date;
        endDate: Date;
      };
      pageIndex: number;
      pageSize: number;
      sortColumn: string | null;
      sortDirection: 'asc' | 'desc' | '';
    };
  };
  mostProfitableCustomers?: CustomerNodeTreeMap[];
  vehicleUtilizationMetrics?: VehicleUtilizationMetric[];
  onlineOfflineDeviceCount?: {
    online: number;
    offline: number;
  };
  
  lastUpdated?: Date;
  executionMetrics?: TimeInsightsMetadata;
  
  // Organizations for behalf functionality
  partnerOrganizations: any[];
  onboardedOrganizations: any[];
  behalfOrgDetails: EntityMap<string, { logo: string; orgName: string }>;
  
  // Newly created operation ID
  newlyCreatedOperationId: string | null;
}

// Uncommented example config for dashboardRows
export const DASHBOARD_ROWS_EXAMPLE: DashboardRow[] = [
  {
    id: 1,
    columns: [
      { id: 'col-1', items: [ { id: 'a', type: 'customerHighestRevenue', data: { customers: [] } } ] }
    ]
  },
  {
    id: 2,
    columns: [
      { id: 'col-1', items: [ { id: 'c', type: '', data: {} } ] },
      { id: 'col-2', items: [ { id: 'b', type: 'pieChart', data: {} } ] }
    ]
  },
  {
    id: 3,
    columns: [
      { id: 'col-1', items: [ { id: 'd', type: 'jobSummary', data: {} } ] }
    ]
  }
];

// Sample customers data
export const DASHBOARD_CUSTOMERS_EXAMPLE: any[] = [
  { name: 'Customer A', value: 250000, percent: 6.5, icon: 'assets/images/customer-logo.svg', positive: true, size: 220 },
  { name: 'Customer B', value: 1800, percent: -6.5, icon: 'assets/images/customer-logo.svg', positive: false, size: 180 },
  { name: 'Customer C', value: 2200, percent: 6.5, icon: 'assets/images/customer-logo.svg', positive: true, size: 200 },
  { name: 'Customer D', value: 1600, percent: -6.5, icon: 'assets/images/customer-logo.svg', positive: false, size: 160 },
  { name: 'Customer E', value: 500000, percent: 6.5, icon: 'assets/images/customer-logo.svg', positive: true, size: 240 }
];
